/**
 * Spx Color Modal - 处理 Color 选项弹窗的开关和变体选择
 */

class SpxColorModal {
  constructor() {
    this.modal = document.getElementById('spx-color-dialog');
    this.currentProductId = null;
    this.init();
  }

  init() {
    // 直接绑定按钮点击事件（支持嵌套在自定义组件中的按钮）
    this.bindButtons();

    // 监听动态添加的按钮
    const observer = new MutationObserver(() => {
      this.bindButtons();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // 监听变体选择变化
    document.addEventListener('change', this.handleVariantChange.bind(this));

    // 关闭按钮事件（使用事件委托统一处理所有点击）
    if (this.modal) {
      this.modal.addEventListener('click', this.handleModalClick.bind(this));
    }

    // Dev 模式：URL 包含 #dev 时自动打开第一个产品
    if (window.location.hash === '#dev') {
      setTimeout(() => this.autoOpenForDev(), 500);
    }
  }

  /**
   * Dev 模式自动打开 modal（用于开发调试）
   */
  autoOpenForDev() {
    const firstBtn = document.querySelector('[data-spx-color-trigger]');
    if (firstBtn) {
      console.log('[SpxColorModal] Dev mode: auto opening first product');
      firstBtn.click();
    } else {
      console.log('[SpxColorModal] Dev mode: no trigger button found');
    }
  }

  /**
   * 绑定所有 color modal 触发按钮
   */
  bindButtons() {
    const buttons = document.querySelectorAll('[data-spx-color-trigger]');
    buttons.forEach(btn => {
      if (!btn.dataset.spxBound) {
        btn.dataset.spxBound = 'true';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const productId = btn.dataset.productId;
          const variantId = btn.dataset.variantId;
          const container = btn.closest('.spx-color-trigger');
          const optionValuesStr = container?.dataset.optionValues;
          const variantsStr = container?.dataset.productVariants;
          const colorPatternStr = container?.dataset.colorPattern;

          if (optionValuesStr && variantsStr) {
            try {
              const optionsWithValues = JSON.parse(optionValuesStr);
              const variants = JSON.parse(variantsStr);

              // 找到 Color 选项
              const colorOption = optionsWithValues.find(opt =>
                opt.name.toLowerCase().includes('color')
              );

              const productData = {
                id: productId,
                title: btn.dataset.productTitle,
                url: btn.dataset.productUrl,
                price: btn.closest('.spx-color-trigger')?.querySelector('.price')?.textContent || '',
                featured_image: variants.find(v => v.id.toString() === variantId)?.featured_media?.preview_image?.src || '',
                colorOption: colorOption,
                variants: variants
              };

              this.currentProductId = productId;

              // 传递原始数据以便绑定到 dialog
              const rawData = {
                productId: productId,
                productTitle: btn.dataset.productTitle,
                productUrl: btn.dataset.productUrl,
                variantId: variantId,
                optionValues: optionValuesStr,
                productVariants: variantsStr,
                colorPattern: colorPatternStr || ''
              };

              this.openModal(productData, variantId, colorOption, rawData);
            } catch (err) {
              console.error('Error parsing product data:', err);
            }
          }
        });
      }
    });
  }

  /**
   * 打开 Modal - 使用预加载的产品数据
   */
  openModal(productData, selectedVariantId, colorOption, rawData) {
    if (!this.modal) {
      console.error('Spx Color Modal not found');
      return;
    }

    const dialog = this.modal.querySelector('dialog');
    if (!dialog) return;

    // 将原始数据绑定到 dialog 元素上
    if (rawData) {
      dialog.dataset.productId = rawData.productId || '';
      dialog.dataset.productTitle = rawData.productTitle || '';
      dialog.dataset.productUrl = rawData.productUrl || '';
      dialog.dataset.variantId = rawData.variantId || '';
      dialog.dataset.optionValues = rawData.optionValues || '';
      dialog.dataset.productVariants = rawData.productVariants || '';
      dialog.dataset.colorPattern = rawData.colorPattern || '';
    }

    // 触发自定义事件，让外部可以监听并手动渲染
    dialog.dispatchEvent(new CustomEvent('spx:modal:open', {
      bubbles: true,
      detail: {
        productData,
        selectedVariantId,
        colorOption,
        rawData
      }
    }));

    // 直接渲染内容
    this.renderModalContent(dialog, productData, rawData);

    // 显示 modal
    this.modal.showDialog?.() || this.showDialogFallback();
  }

  /**
   * 渲染 Modal 内容
   */
  renderModalContent(dialog, productData, rawData) {
    if (!dialog) return;

    // 渲染产品标题
    const titleEl = dialog.querySelector('[data-product-title]');
    if (titleEl) {
      titleEl.textContent = productData?.title || '';
    }

    // 渲染价格
    const priceEl = dialog.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.textContent = productData?.price || '';
    }

    // 渲染 colorPattern
    const colorOptionsEl = dialog.querySelector('[data-color-options]');
    if (!colorOptionsEl) return;

    const patterns = this.parseJsonSafely(rawData?.colorPattern, []);
    const variants = this.parseJsonSafely(rawData?.productVariants, []);
    if (!Array.isArray(patterns) || patterns.length === 0) {
      colorOptionsEl.innerHTML = '';
      return;
    }

    const escapeHtml = (value) => {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    };

    // 格式化价格
    const formatPrice = (price) => {
      if (!price) return '';
      return '$' + (price / 100).toFixed(2);
    };

    // 计算折扣百分比
    const calculateDiscount = (original, current) => {
      if (!original || !current) return null;
      const discount = Math.round(((original - current) / original) * 100);
      return discount > 0 ? discount : null;
    };

    colorOptionsEl.innerHTML = `
      <div class="spx-color-pattern-list w-full md:px-6 ">
        ${patterns
        .map((pattern) => {
          const label = escapeHtml(pattern?.label || pattern?.name || '');
          const color = escapeHtml(pattern?.color || '');
          // 找到对应颜色的变体价格
          const variant = variants.find(v =>
            v.options && v.options.includes(pattern?.label || pattern?.name)
          );
          const variantId = variant?.id || '';
          const currentPrice = variant?.price ? formatPrice(variant.price) : '';
          const originalPrice = variant?.compare_at_price ? formatPrice(variant.compare_at_price) : '';
          const hasDiscount = originalPrice && currentPrice !== originalPrice;
          const discountPercent = hasDiscount ? calculateDiscount(variant?.compare_at_price, variant?.price) : null;
          return `
              <div class="spx-color-pattern-item flex gap-3 items-center justify-between md:px-2 py-2 hover:bg-(--color-fill) rounded-[8px]   w-full" data-variant-id="${variantId} hover:bg-(--color-fill)">
                <div class="flex gap-2 items-center  p-2 border border-(--color-border) rounded-[8px] w-[120px] bg-white ">
                  <span class="spx-color-pattern-swatch w-7 h-7 rounded-full border border-gray-200" style="--spx-color-pattern:${color};"></span>
                  <span class="spx-color-pattern-label text-sm font-medium">${color}</span>
                </div>
                <div class="flex gap-3 items-center flex-1">
                  <div class="spx-color-pattern-price flex flex-col md:flex-row gap-2 items-center rounded-[8px]">
                    <span class="current-price font-medium text-md">${currentPrice}</span>
                    ${hasDiscount ? `<span class="original-price line-through text-gray-400 text-md">${originalPrice}</span>` : ''}
                    ${discountPercent ? `<span class="discount-badge font-normal bg-green-500 text-white text-sm px-3 py-1 rounded-[8px]">-${discountPercent}%</span>` : ''}
                  </div>
                  <div class="quantity-controller flex items-center gap-1 ml-auto  ">
                    <button type="button" class="qty-btn  cursor-pointer qty-minus w-8 h-8 rounded-full border border-(--color-border) bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100" data-action="decrease">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </button>
                    <input type="number" class=" qty-input w-10 text-center text-sm font-medium border-0 p-0" value="0" min="0" readonly>
                    <button type="button" class="qty-btn  cursor-pointer qty-plus w-8 h-8 rounded-full border border-(--color-border) bg-white text-gray-800 flex items-center justify-center hover:bg-gray-800 hover:text-white" data-action="increase">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2V12M2 7H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            `;
        })
        .join('')}
      </div>
    `;

    // 初始化总价显示
    this.updateTotalPrice();

    if (priceEl) {
      priceEl.innerHTML = `
        <span class="spx-color-pattern-price">${productData?.price || ''}</span>
      `;
    }
  }

  /**
   * @param {unknown} value
   * @param {unknown} fallback
   * @returns {unknown}
   */
  parseJsonSafely(value, fallback = null) {
    if (value == null || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  }

  /**
   * 打开 Modal - 使用 URL 获取（备用方案）
   */
  async openModalFromUrl(productId, productTitle, productUrl, variantId) {
    if (!this.modal) {
      console.error('Spx Color Modal not found');
      return;
    }

    // 更新当前产品信息
    await this.updateModalContentFromUrl(productId, productUrl, variantId);

    // 显示 modal
    this.modal.showDialog?.() || this.showDialogFallback();
  }

  /**
   * 关闭 Modal
   */
  closeModal() {
    if (!this.modal) return;
    this.modal.closeDialog?.() || this.closeDialogFallback();
  }

  /**
   * 回退显示方案
   */
  showDialogFallback() {
    const dialog = this.modal.querySelector('dialog');
    if (dialog) {
      dialog.showModal();
      dialog.setAttribute('open', '');
    }
  }

  /**
   * 回退关闭方案
   */
  closeDialogFallback() {
    const dialog = this.modal.querySelector('dialog');
    if (dialog) {
      dialog.close();
      dialog.removeAttribute('open');
    }
  }

  /**
   * 更新 Modal 内容 - 使用预加载的产品数据
   */
  updateModalContentFromData(productData, selectedVariantId, colorOption) {
    const contentContainer = this.modal?.querySelector('#spx-color-modal-content');
    if (!contentContainer) return;

    // 从 colorOption 构建变体数据
    const variantData = [];
    if (colorOption && colorOption.values) {
      colorOption.values.forEach(value => {
        // 找到对应这个选项值的变体
        const variant = productData.variants.find(v =>
          v.options && v.options.includes(value)
        );
        if (variant) {
          variantData.push({
            id: variant.id.toString(),
            name: value,
            image: variant.featured_media?.preview_image?.src || '',
            checked: variant.id.toString() === selectedVariantId
          });
        }
      });
    }

    // 构建 modal 内容
    contentContainer.innerHTML = this.buildModalContent({
      title: productData.title,
      price: productData.price,
      image: productData.featured_image,
      variants: variantData,
      selectedVariantId: selectedVariantId
    });
  }

  /**
   * 更新 Modal 内容 - 从产品页面获取（备用方案）
   */
  async updateModalContentFromUrl(productId, productUrl, selectedVariantId) {
    const contentContainer = this.modal?.querySelector('#spx-color-modal-content');
    if (!contentContainer || !productUrl) return;

    try {
      // 获取产品页面内容
      const response = await fetch(productUrl);
      if (!response.ok) throw new Error('Failed to fetch product');

      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      // 提取产品信息
      const productTitle = doc.querySelector('h1')?.textContent?.trim();
      const productPrice = doc.querySelector('.price')?.textContent?.trim();
      const productImage = doc.querySelector('[data-product-featured-image]')?.getAttribute('src')
        || doc.querySelector('.product-media img')?.getAttribute('src');

      // 提取 color 变体选项
      const variantData = this.extractVariantDataFromDoc(doc, selectedVariantId);

      // 构建 modal 内容
      contentContainer.innerHTML = this.buildModalContent({
        title: productTitle,
        price: productPrice,
        image: productImage,
        variants: variantData,
        selectedVariantId: selectedVariantId
      });

    } catch (error) {
      console.error('Error loading product content:', error);
      contentContainer.innerHTML = '<p class="spx-color-modal__error">加载产品信息失败，请重试</p>';
    }
  }

  /**
   * 从产品页面提取变体数据
   */
  extractVariantDataFromDoc(doc, selectedVariantId) {
    const variants = [];

    // 尝试从 variant picker 中提取 color 选项
    const colorInputs = doc.querySelectorAll('input[name*="Color"], input[name*="color"]');
    colorInputs.forEach(input => {
      variants.push({
        id: input.value,
        name: input.getAttribute('data-value') || input.value,
        checked: input.value === selectedVariantId
      });
    });

    // 如果没有找到，尝试从 product 数据中提取
    if (variants.length === 0) {
      const scriptEl = doc.querySelector('[data-product-json]');
      if (scriptEl) {
        try {
          const productData = JSON.parse(scriptEl.textContent);
          const colorOption = productData.options_with_values?.find(opt =>
            opt.name.toLowerCase().includes('color')
          );
          if (colorOption) {
            colorOption.values.forEach(value => {
              const variant = productData.variants.find(v =>
                v.options.includes(value)
              );
              if (variant) {
                variants.push({
                  id: variant.id.toString(),
                  name: value,
                  checked: variant.id.toString() === selectedVariantId
                });
              }
            });
          }
        } catch (e) {
          console.error('Error parsing product JSON:', e);
        }
      }
    }

    return variants;
  }

  /**
   * 构建 Modal 内容 HTML
   */
  buildModalContent({ title, price, image, variants, selectedVariantId }) {
    let variantsHtml = '';
    if (variants.length > 0) {
      variantsHtml = `
        <div class="spx-color-modal__variants">
          <p class="spx-color-modal__variants-label">选择颜色:</p>
          <div class="spx-color-modal__options">
            ${variants.map(v => `
              <label class="spx-color-modal__option">
                <input 
                  type="radio" 
                  name="color_variant" 
                  value="${v.id}"
                  data-variant-id="${v.id}"
                  class="spx-color-modal__input"
                  ${v.checked ? 'checked' : ''}
                >
                <span class="spx-color-modal__option-label">${v.name}</span>
              </label>
            `).join('')}
          </div>
        </div>
        <input type="hidden" name="id" class="spx-color-modal__variant-id" value="${selectedVariantId}">
        <button type="submit" class="spx-btn spx-color-modal__add-btn">加入购物车</button>
      `;
    }

    return `
      <div class="spx-color-modal__product">
        ${image ? `<div class="spx-color-modal__media"><img src="${image}" alt="${title}" loading="lazy"></div>` : ''}
        <div class="spx-color-modal__info">
          ${title ? `<h3 class="spx-color-modal__title">${title}</h3>` : ''}
          ${price ? `<p class="spx-color-modal__price">${price}</p>` : ''}
        </div>
      </div>
      <form method="post" action="/cart/add" class="spx-color-modal__form" accept-charset="UTF-8" enctype="multipart/form-data">
        ${variantsHtml}
      </form>
    `;
  }

  /**
   * 处理变体选择变化
   */
  handleVariantChange(e) {
    if (!e.target.matches('.spx-color-modal__input')) return;

    const variantId = e.target.dataset.variantId;
    if (variantId) {
      this.updateVariantIdInput(variantId);
      this.updateProductImage(e.target);
    }
  }

  /**
   * 更新隐藏的 variant ID 输入框
   */
  updateVariantIdInput(variantId) {
    const input = this.modal?.querySelector('.spx-color-modal__variant-id');
    if (input) {
      input.value = variantId;
    }
  }

  /**
   * 更新产品图片
   */
  updateProductImage(radioInput) {
    // 可以在这里添加更新产品图片的逻辑
    // 根据选中的 color 变体更新图片
  }

  /**
   * 绑定数量控制按钮事件
   */
  bindQuantityControls(container) {
    if (!container) return;
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;

      const item = btn.closest('.spx-color-pattern-item');
      const input = item?.querySelector('.qty-input');
      if (!input) return;

      let qty = parseInt(input.value, 10) || 0;
      const action = btn.dataset.action;

      if (action === 'increase') {
        qty += 1;
      } else if (action === 'decrease' && qty > 0) {
        qty -= 1;
      }

      input.value = qty;
      this.updateTotalPrice();
    });
  }

  /**
   * 更新总价格和总数量
   */
  updateTotalPrice() {
    const container = this.modal?.querySelector('[data-quantity-controller]');
    const items = this.modal?.querySelectorAll('.spx-color-pattern-item');

    let total = 0;
    let totalQty = 0;

    items?.forEach(item => {
      const qty = parseInt(item.querySelector('.qty-input')?.value, 10) || 0;
      const priceText = item.querySelector('.current-price')?.textContent || '';
      const price = parseFloat(priceText.replace(/[^0-9.]/g, '')) || 0;
      total += qty * price;
      totalQty += qty;
    });

    if (container) {
      container.innerHTML = `
        <div class="flex gap-4 items-center justify-start mt-4 pt-4 p border-t border-(--color-border)">
         <div class="total-price text-md font-medium">
            Final Price: <span class="pl-2text-lg font-semibold">$${total.toFixed(2)}</span>
          </div>
          <div class="total-quantity text-md font-medium">
            Quantity: <span class="pl-2 text-lg font-semibold">${totalQty}</span>
          </div>
         
        </div>
      `;
    }
  }

  /**
   * 处理 Modal 内部点击（关闭按钮、数量按钮、Add to Cart 等）
   */
  handleModalClick(e) {
    // 点击关闭按钮
    if (e.target.closest('.spx-color-modal__close')) {
      this.closeModal();
      return;
    }

    // 点击数量控制按钮（+/-）
    const qtyBtn = e.target.closest('.qty-btn');
    if (qtyBtn) {
      const item = qtyBtn.closest('.spx-color-pattern-item');
      const input = item?.querySelector('.qty-input');
      if (!input) return;

      let qty = parseInt(input.value, 10) || 0;
      const action = qtyBtn.dataset.action;

      if (action === 'increase') {
        qty += 1;
      } else if (action === 'decrease' && qty > 0) {
        qty -= 1;
      }

      input.value = qty;
      this.updateTotalPrice();
      return;
    }

    // 点击 Add to Cart 按钮（使用 .add-to-cart-btn 类）
    const addToCartBtn = e.target.closest('.add-to-cart-btn');
    if (addToCartBtn) {
      this.handleAddToCart(addToCartBtn);
      return;
    }

    // 点击 modal 背景关闭（可选）
    if (e.target === e.currentTarget) {
      this.closeModal();
    }
  }

  /**
   * 处理批量添加到购物车
   */
  async handleAddToCart(button) {
    const items = this.collectCartItems();
    
    if (items.length === 0) {
      this.showStatus('Please select at least one item', 'error');
      return;
    }

    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const result = await this.addItemsToCart(items);
      this.showStatus(`Added ${items.length} item(s) to cart`, 'success');
      
      // 触发主题标准 cart:update 事件，通知购物车抽屉等组件刷新
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: result,
          sourceId: 'spx-color-modal',
          data: {
            source: 'spx-color-modal',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            sections: result.sections
          }
        }
      }));

      // 可选：关闭弹窗或重置数量
      // this.closeModal();
      
    } catch (error) {
      console.error('[SpxColorModal] Add to cart error:', error);
      this.showStatus('Failed to add items. Please try again.', 'error');
    } finally {
      button.disabled = false;
      button.textContent = 'Add to Cart';
    }
  }

  /**
   * 收集购物车商品
   */
  collectCartItems() {
    const items = [];
    const itemElements = this.modal?.querySelectorAll('.spx-color-pattern-item');
    
    itemElements?.forEach(item => {
      const qty = parseInt(item.querySelector('.qty-input')?.value, 10) || 0;
      const variantId = item.dataset.variantId;
      
      if (qty > 0 && variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: qty
        });
      }
    });
    
    return items;
  }

  /**
   * AJAX 批量添加到购物车
   */
  async addItemsToCart(items) {
    const formData = { items };
    
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add items to cart');
    }

    return response.json();
  }

  /**
   * 显示状态信息
   */
  showStatus(message, type = 'info') {
    const statusEl = this.modal?.querySelector('[data-status]');
    if (!statusEl) return;
    
    statusEl.textContent = message;
    statusEl.classList.remove('hidden', 'text-green-600', 'text-red-600', 'text-gray-600');
    
    const colorClass = type === 'success' ? 'text-green-600' : 
                       type === 'error' ? 'text-red-600' : 'text-gray-600';
    statusEl.classList.add(colorClass);
    
    // 3秒后自动隐藏
    setTimeout(() => {
      statusEl.classList.add('hidden');
    }, 3000);
  }
}

// 初始化
if (!window.spxColorModal) {
  window.spxColorModal = new SpxColorModal();
}
